class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
          
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);

// === Accessibility updates (Feb 2026) ===
// - Trap keyboard focus inside cart drawer panel while open
// - Focus discount input when discount disclosure opens
// - Focus notes textarea when notes disclosure opens
(() => {
  const CART_DRAWER_DETAILS_SELECTOR = 'details.cart-drawer-container';
  const MINI_ACTION_SELECTOR = 'details.mini-cart__action.disclosure-has-popup[data-action]';

  const NOTES_FIELD_SELECTOR = 'textarea#Cart-note, textarea[name="note"], textarea';
  const DISCOUNT_FIELD_SELECTOR = 'input#CartDiscountForm-mini-cart, input[name="discount"], input[type="text"]';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let trapRoot = null;          // the panel we trap within (NOT the details)
  let drawerDetails = null;     // the <details> that controls open/close
  let previouslyFocused = null;

  const isVisible = (el) =>
    !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
  }

  function focusFirst(container) {
    const focusables = getFocusable(container);
    if (focusables.length) {
      focusables[0].focus();
      return;
    }
    // Fallback: focus container
    container.setAttribute('tabindex', '-1');
    container.focus();
  }

  function trapTab(e) {
    if (!trapRoot || e.key !== 'Tab') return;

    const focusables = getFocusable(trapRoot);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const current = document.activeElement;

    // If focus escaped trapRoot (but drawer is still open), pull it back in
    if (!trapRoot.contains(current)) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (e.shiftKey && current === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    trapTab(e);

    // Esc closes the cart drawer
    if (drawerDetails && drawerDetails.open && e.key === 'Escape') {
      drawerDetails.open = false;
    }
  }

  function getDrawerPanel(detailsEl) {
    // The drawer panel is normally the element immediately after <summary>
    const summary = detailsEl.querySelector('summary');
    if (!summary) return null;
    return summary.nextElementSibling;
  }

  function activateDrawerTrap(detailsEl) {
    drawerDetails = detailsEl;
    const panel = getDrawerPanel(detailsEl);

    // If we can’t find a panel, do nothing (don’t break Tab)
    if (!panel) return;

    trapRoot = panel;
    previouslyFocused = document.activeElement;

    // Put focus into the panel (not the summary)
    focusFirst(panel);

    document.addEventListener('keydown', onKeydown, true);
  }

  function deactivateDrawerTrap() {
    document.removeEventListener('keydown', onKeydown, true);

    // Restore focus to whatever was focused before opening
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }

    previouslyFocused = null;
    trapRoot = null;
    drawerDetails = null;
  }

  function focusFieldWhenOpened(detailsEl, fieldSelector) {
    // Wait for CSS disclosure (summary + *) to become visible
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const field = detailsEl.querySelector(fieldSelector);
        if (field && isVisible(field)) {
          field.focus();
          if (typeof field.setSelectionRange === 'function' && field.value != null) {
            const len = field.value.length;
            field.setSelectionRange(len, len);
          }
        }
      });
    });
  }

  function initCartDrawerTrap() {
    const detailsEl = document.querySelector(CART_DRAWER_DETAILS_SELECTOR);
    if (!detailsEl) return;

    // Bind once
    if (detailsEl.__a11yBound) return;
    detailsEl.__a11yBound = true;

    detailsEl.addEventListener('toggle', () => {
      if (detailsEl.open) activateDrawerTrap(detailsEl);
      else deactivateDrawerTrap();
    });

    // If it loads already open
    if (detailsEl.open) activateDrawerTrap(detailsEl);
  }

  function initDisclosureFocus() {
    document.querySelectorAll(MINI_ACTION_SELECTOR).forEach((actionDetails) => {
      if (actionDetails.__a11yBound) return;
      actionDetails.__a11yBound = true;

      actionDetails.addEventListener('toggle', () => {
        if (!actionDetails.open) return;

        const action = (actionDetails.dataset.action || '').toLowerCase();
        if (action === 'notes' || action === 'note') {
          focusFieldWhenOpened(actionDetails, NOTES_FIELD_SELECTOR);
        } else if (action === 'discount') {
          focusFieldWhenOpened(actionDetails, DISCOUNT_FIELD_SELECTOR);
        }
      });
    });
  }

  function initAll() {
    initCartDrawerTrap();
    initDisclosureFocus();
  }

  // Run now…
  initAll();

  // …and run again whenever mini-cart content is injected/refreshed
  document.addEventListener('cartdrawer:opened', initAll);
})();
